import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StripeStepper, Provider } from '../../components'
import { PatchData, PatchContent } from 'src/model/patch'
import {
  patchCollectionSettingsAction,
  patchCollectionPaymentMessagesAction,
} from 'src/reducers/collection/action'
import {
  patchCertificatePaymentSettingAction,
  patchCertificatePriceAction,
} from 'src/reducers/certificate/action'
import { patchPriceAction } from 'src/reducers/collection/action'
import PaymentMessageEditor from '../payment-messages/payment-message-editor'
import PaymentGateway from 'src/model/payment-providers'
import { Typography, withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import styles from './styles'

// Helper function: Sayıyı string'e çevir
const getPaymentProviderKey = value => {
  if (!value) return 'stripe'
  return (
    Object.keys(PaymentGateway).find(key => PaymentGateway[key] === value) ||
    'stripe'
  )
}

const PaidAccess = ({
  priceData: priceData,
  paymentSettingsConfigured: initialPaymentSettingsConfigured,
  entity,
  paymentLink: initialPaymentLink,
  setPaymentLink,
  setPaymentSettingsConfigured,
  setPaymentProvider,
  onClickDone,
  handleBack,
  showBackButton,
  collectionId,
  classes,
  collectionEditCard,
  paymentSettingType = 'collection',
  bannerTitle,
  hideBanner,
  settingsLoaded = true,
}) => {
  const dispatch = useDispatch()
  const collectionSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const certificatePaymentSetting = useSelector(
    state => state.certificate?.paymentSetting,
  )
  const isCertificate = paymentSettingType === 'certificate'

  // Normalize the shape across collection-paywall and certificate-paywall sources so the wizard
  // doesn't need to care which slice it's reading from.
  const settings = isCertificate
    ? {
        paymentProvider: certificatePaymentSetting?.paymentProvider,
        paymentLink: certificatePaymentSetting?.paymentLink,
        isPaymentConfigured:
          certificatePaymentSetting?.isPaymentConfigured ?? false,
        price: certificatePaymentSetting
          ? {
              priceType: certificatePaymentSetting.priceType,
              amount: certificatePaymentSetting.amount,
              currency: certificatePaymentSetting.currency,
              priceMetadata: certificatePaymentSetting.priceMetadata,
            }
          : null,
        messageOnSuccess: certificatePaymentSetting?.messageOnSuccess,
        messageOnFailure: certificatePaymentSetting?.messageOnFailure,
      }
    : collectionSettings

  const targetId = collectionId ? collectionId : entity.id

  const dispatchPatchConfig = (patchSource, callback) => {
    if (isCertificate) {
      dispatch(
        patchCertificatePaymentSettingAction(targetId, patchSource, callback),
      )
    } else {
      dispatch(patchCollectionSettingsAction(patchSource, callback))
    }
  }

  const dispatchPatchPrice = (id, priceDataPayload, callback) => {
    if (isCertificate) {
      dispatch(patchCertificatePriceAction(id, priceDataPayload, callback))
    } else {
      dispatch(patchPriceAction(id, priceDataPayload, callback))
    }
  }

  const handlePatchMessage = (path, value, callback) => {
    const patch = new PatchData(
      new PatchContent(value, path, PatchData.OPERATION_REPLACE),
      targetId,
    )
    if (isCertificate) {
      dispatch(patchCertificatePaymentSettingAction(targetId, patch, callback))
    } else {
      dispatch(patchCollectionPaymentMessagesAction(targetId, patch, callback))
    }
  }

  const messages = isCertificate
    ? {
        success: certificatePaymentSetting?.messageOnSuccess,
        failure: certificatePaymentSetting?.messageOnFailure,
      }
    : {
        success: collectionSettings?.paymentMessageOnSuccess,
        failure: collectionSettings?.paymentMessageOnFailure,
      }

  // İlk render flag'i
  const isInitialMount = useRef(true)

  // Redux'tan gelen sayı değerini string'e çevir
  const [localPaymentProvider, setLocalPaymentProvider] = React.useState(
    getPaymentProviderKey(settings?.paymentProvider),
  )

  const [
    localPaymentSettingsConfigured,
    setLocalPaymentSettingsConfigured,
  ] = React.useState(initialPaymentSettingsConfigured)
  const [localPaymentLink, setLocalPaymentLink] = React.useState(
    initialPaymentLink,
  )
  const [providerChangeCount, setProviderChangeCount] = React.useState(0)

  useEffect(() => {
    // For certificate mode the GET resolves async after mount, so the mount-only
    // initialiser would race the response and could destructively default-write
    // a stripe provider on top of a saved row. Wait until the saved provider is
    // visible (or stays absent for collection mode where settings load eagerly).
    if (!isInitialMount.current) {
      return
    }

    if (!settingsLoaded) {
      // GET hasn't resolved yet — defer the auto-init so we don't trample a
      // saved row with the default stripe write before we've seen its values.
      return
    }

    isInitialMount.current = false

    if (!settings?.paymentProvider) {
      handleChange('stripe')
    } else {
      const providerKey = getPaymentProviderKey(settings.paymentProvider)
      setLocalPaymentProvider(providerKey)
    }
  }, [settingsLoaded, settings?.paymentProvider])

  // Sync local mirrors when the settings prop changes (e.g. async GET resolves
  // after mount, or another component re-fetches the row).
  useEffect(() => {
    if (settings?.paymentProvider) {
      setLocalPaymentProvider(getPaymentProviderKey(settings.paymentProvider))
    }
  }, [settings?.paymentProvider])

  useEffect(() => {
    setLocalPaymentSettingsConfigured(settings?.isPaymentConfigured ?? false)
  }, [settings?.isPaymentConfigured])

  useEffect(() => {
    setLocalPaymentLink(settings?.paymentLink ?? '')
  }, [settings?.paymentLink])

  const handleChange = eventOrValue => {
    const selectedValue =
      typeof eventOrValue === 'string'
        ? eventOrValue
        : eventOrValue?.target?.value

    setLocalPaymentProvider(selectedValue)

    setPaymentProvider && setPaymentProvider(selectedValue)

    setProviderChangeCount(prev => prev + 1)

    const patchSource = new PatchData(
      null,
      collectionId ? collectionId : entity.id,
    )
    patchSource.addContent(
      new PatchContent(
        PaymentGateway[selectedValue],
        'paymentProvider',
        PatchData.OPERATION_REPLACE,
      ),
    )
    patchSource.addContent(
      new PatchContent(null, 'paymentLink', PatchData.OPERATION_REPLACE),
    )
    patchSource.addContent(
      new PatchContent(
        false,
        'isPaymentConfigured',
        PatchData.OPERATION_REPLACE,
      ),
    )

    setLocalPaymentLink('')
    setPaymentLink && setPaymentLink('')

    setLocalPaymentSettingsConfigured(false)
    setPaymentSettingsConfigured && setPaymentSettingsConfigured(false)

    dispatchPatchConfig(patchSource, response => {
      console.log('Patch response:', response)
    })
  }

  const handleLocalClickDone = clickedBeforeFinish => {
    setLocalPaymentSettingsConfigured(true)
    // For certificate mode the parent (PaymentSetup) does not own a persistence
    // callback the way PaymentWall does for collection mode, so flip the backend
    // flag here. For collection mode PaymentWall still handles the same write.
    if (isCertificate) {
      const patchSource = new PatchData(
        new PatchContent(true, 'isPaymentConfigured', PatchData.OPERATION_REPLACE),
        targetId,
      )
      dispatchPatchConfig(patchSource)
    }
    onClickDone && onClickDone(clickedBeforeFinish)
  }

  return (
    <>
      {!hideBanner && (
        <div className={classes.paidAccessBanner}>
          <div className={classes.paidAccessBannerContent}>
            <Icon icon="fluent:key-24-regular" width="20px" height="20px" />
            <Typography className={classes.paidAccessTitle}>
              {bannerTitle ?? 'Paid access'}
            </Typography>
          </div>
          {showBackButton && (
            <CoreButton
              size="small"
              color="info"
              variant="contained"
              onClick={handleBack}
            >
              Change access type
            </CoreButton>
          )}
        </div>
      )}

      <div className={classes.providerContainer}>
        <Provider
          paymentProvider={localPaymentProvider}
          handleChange={handleChange}
        />
      </div>

      {localPaymentProvider != null && (
        <StripeStepper
          collectionId={collectionId}
          priceData={priceData}
          entity={entity}
          paymentProvider={localPaymentProvider}
          providerChangeCount={providerChangeCount}
          paymentLink={localPaymentLink}
          setPaymentLink={setLocalPaymentLink}
          paymentSettingsConfigured={localPaymentSettingsConfigured}
          setPaymentSettingsConfigured={setLocalPaymentSettingsConfigured}
          onClickDone={handleLocalClickDone}
          settings={settings}
          onPatchConfig={dispatchPatchConfig}
          onPatchPrice={dispatchPatchPrice}
          paymentSettingType={paymentSettingType}
        />
      )}

      {localPaymentSettingsConfigured && (
        <PaymentMessageEditor
          messages={messages}
          onPatch={handlePatchMessage}
        />
      )}
    </>
  )
}

export default withStyles(styles)(PaidAccess)
